import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Pencil, X, Check, Calendar, ChevronRight } from "lucide-react";
import { api, BRL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MoneyInput from "@/components/MoneyInput";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + (iso.length === 10 ? "T12:00:00" : ""));
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
};

/**
 * Dialog: consolidar lançamentos FDP com IA.
 * 1) Pede preview pro backend
 * 2) Mostra grupos editáveis
 * 3) Admin aprova → cria itens oficiais
 */
export default function ConsolidateDialog({ open, onOpenChange, budget, onDone }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [groups, setGroups] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [entriesCount, setEntriesCount] = useState(0);
  const [editIdx, setEditIdx] = useState(null); // null | index

  const loadPreview = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/fdp-entries/consolidate-preview?budget_id=${budget.id}`);
      setGroups(r.data?.groups || []);
      setGrandTotal(r.data?.grand_total || 0);
      setEntriesCount(r.data?.entries_count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar preview");
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (open) {
      setGroups([]);
      setGrandTotal(0);
      setEditIdx(null);
      loadPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const recomputeTotal = (gs) =>
    gs.reduce((acc, g) => acc + Number(g.total || 0), 0);

  const applyEdit = (idx, patch) => {
    setGroups((prev) => {
      const next = prev.map((g, i) => {
        if (i !== idx) return g;
        const merged = { ...g, ...patch };
        // Mantém o total intocado (soma dos lançamentos). Recalcula só o unit.
        const qty = Math.max(1, Number(merged.quantity ?? g.quantity) || 1);
        const total = Number(g.total || 0); // total é imutável, vem do backend
        merged.quantity = qty;
        merged.unit_value = Number((total / qty).toFixed(2));
        merged.total = total;
        return merged;
      });
      setGrandTotal(recomputeTotal(next));
      return next;
    });
  };

  const removeGroup = (idx) => {
    toast.error("Não é possível remover um grupo durante a consolidação — todos os lançamentos devem ser agrupados. Edite a descrição se preferir.");
  };

  const applyConsolidation = async () => {
    if (!groups.length) { toast.error("Nenhum grupo para consolidar"); return; }
    setApplying(true);
    try {
      const r = await api.post("/fdp-entries/apply-consolidation", {
        budget_id: budget.id,
        groups: groups.map((g) => ({
          description: g.description,
          quantity: Number(g.quantity) || 1,
          unit_value: Number(g.unit_value) || 0,
          entry_ids: g.entry_ids,
        })),
      });
      toast.success(`${r.data.items_created} itens criados · ${r.data.entries_consolidated} lançamentos consolidados`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao consolidar");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="consolidate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            Consolidar lançamentos com IA
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            A IA agrupa serviços semelhantes em itens consolidados. Você pode editar antes de confirmar.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-10 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mb-2 text-indigo-600" />
            <p className="text-sm">Analisando lançamentos com IA...</p>
            <p className="text-[11px] text-slate-400 mt-1">Pode levar alguns segundos</p>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="bg-gradient-to-br from-indigo-50 to-sky-50 border border-indigo-200 rounded-xl p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Resumo da consolidação</p>
                  <p className="text-sm text-slate-700 mt-0.5">
                    <b>{entriesCount}</b> lançamentos → <b>{groups.length}</b> item{groups.length !== 1 ? "s" : ""} do orçamento
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Total</p>
                  <p className="text-lg font-black text-indigo-900 tabular-nums">{BRL(grandTotal)}</p>
                </div>
              </div>
            </div>

            {/* Guarantia de valor */}
            <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg p-2.5 flex items-start gap-2" data-testid="consolidate-safety">
              <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-[11px] text-emerald-900 leading-snug">
                <p className="font-bold">Soma preservada</p>
                <p>O total consolidado é SEMPRE igual à soma exata dos lançamentos originais ({BRL(grandTotal)}). Você pode editar a descrição e a quantidade, mas o valor total é imutável — garantia contra erro de centavo.</p>
              </div>
            </div>

            {/* Groups */}
            <div className="space-y-2 max-h-[55vh] overflow-y-auto -mx-1 px-1" data-testid="consolidate-groups">
              {groups.length === 0 ? (
                <p className="text-center py-6 text-sm text-slate-500">Nenhum grupo gerado.</p>
              ) : groups.map((g, idx) => (
                <div key={idx} className="border border-slate-200 bg-white rounded-xl p-3" data-testid={`consolidate-group-${idx}`}>
                  {editIdx === idx ? (
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-[11px] font-semibold">Descrição consolidada</Label>
                        <Input
                          value={g.description}
                          onChange={(e) => applyEdit(idx, { description: e.target.value })}
                          className="text-sm"
                          data-testid={`input-group-desc-${idx}`}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-[11px] font-semibold">Quantidade</Label>
                          <Input
                            type="number"
                            min={1}
                            value={g.quantity}
                            onChange={(e) => applyEdit(idx, { quantity: Number(e.target.value) || 1 })}
                            data-testid={`input-group-qty-${idx}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[11px] font-semibold text-slate-400">Valor unitário (auto)</Label>
                          <div className="h-10 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm font-mono text-slate-500 tabular-nums">
                            {BRL(g.unit_value)}
                          </div>
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-500">
                        Total do grupo: <b className="text-indigo-700 tabular-nums">{BRL(g.total)}</b> (travado — soma exata dos lançamentos).
                      </p>
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => setEditIdx(null)}>
                          <Check className="w-3 h-3 mr-1" /> Pronto
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900">{g.description}</p>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            {g.quantity} × {BRL(g.unit_value)} = <b className="text-indigo-700">{BRL(g.total)}</b>
                          </p>
                        </div>
                        <div className="flex items-center gap-0.5 shrink-0">
                          <button
                            onClick={() => setEditIdx(idx)}
                            className="text-slate-400 hover:text-indigo-600 p-1"
                            aria-label="Editar"
                            data-testid={`btn-edit-group-${idx}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {g.entry_preview?.length > 0 && (
                        <details className="mt-2">
                          <summary className="text-[11px] text-indigo-600 cursor-pointer hover:underline">
                            <ChevronRight className="w-3 h-3 inline-block mr-0.5" />
                            Ver {g.entry_preview.length} lançamento(s) agrupado(s)
                          </summary>
                          <ul className="mt-2 space-y-1 pl-2 border-l-2 border-indigo-100">
                            {g.entry_preview.map((ep, i) => (
                              <li key={i} className="text-[11px] text-slate-600 flex items-start gap-1.5">
                                <Calendar className="w-2.5 h-2.5 shrink-0 mt-0.5 text-slate-400" />
                                <span className="shrink-0 text-slate-400">{formatDate(ep.service_date)}</span>
                                <span className="flex-1">{ep.description}</span>
                                <span className="shrink-0 tabular-nums">{BRL(ep.value)}</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            Cancelar
          </Button>
          <Button
            onClick={applyConsolidation}
            disabled={applying || loading || groups.length === 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto"
            data-testid="btn-apply-consolidation"
          >
            {applying ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Aplicando...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-1.5" /> Aplicar e criar itens</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
